import { KEENEY_VERIFICATION_POLICY, KEENEY_EXISTING_DATA_POLICY, isKeeneyVerificationAddress } from "../../supabase/functions/_shared/keeney-verification-policy";
const SESSION_KEY = "fairpath_keeney_verification_v1";
// Tab-local and non-secret. Unlike sessionStorage, this survives clearing site
// storage followed by a same-origin reload; never turns a restriction off.
const TAB_RESTRICTION = "fairpath:keeney-existing-data-only";
function recognizedPolicy(value: unknown): boolean {
  return value === KEENEY_EXISTING_DATA_POLICY || value === KEENEY_VERIFICATION_POLICY;
}
export type KeeneyVerificationMode = "inactive" | "active" | "invalid";
let storageFailedInVerification = false;
let verificationSeenInTab = false;
/** Explicit operator mode persists across normal SPA routes; malformed input never enables a paid fallback. */
export function keeneyVerificationMode(): KeeneyVerificationMode {
  if (typeof window === "undefined") return "inactive";
  if (storageFailedInVerification) return "invalid";
  const query = new URLSearchParams(window.location.search);
  const present = query.has("keeney_verification");
  const value = present ? query.get("keeney_verification") : null;
  if (present) verificationSeenInTab = true;
  try {
    if (window.name === TAB_RESTRICTION) verificationSeenInTab = true;
    if (verificationSeenInTab) window.name = TAB_RESTRICTION;
    if (present) window.sessionStorage.setItem(SESSION_KEY, recognizedPolicy(value) ? KEENEY_EXISTING_DATA_POLICY : "invalid");
    const stored = window.sessionStorage.getItem(SESSION_KEY);
    if (stored !== null) {
      verificationSeenInTab = true;
      window.name = TAB_RESTRICTION;
    }
    if (!present && stored === null) return verificationSeenInTab ? "invalid" : "inactive";
    return recognizedPolicy(present ? value : stored) && recognizedPolicy(stored) ? "active" : "invalid";
  } catch {
    if (verificationSeenInTab) storageFailedInVerification = true;
    return verificationSeenInTab ? "invalid" : "inactive";
  }
}
export function keeneyVerificationSuppressesProviders(): boolean { return keeneyVerificationMode() !== "inactive"; }
/** Central Supabase transport covers ordinary lookup, comparison and receipt-recovery callers. */
export function createKeeneyVerificationFetch(baseFetch: typeof fetch, supabaseUrl: string, readMode = keeneyVerificationMode): typeof fetch {
  const origin = new URL(supabaseUrl).origin;
  return async (input, init) => {
    const mode = readMode();
    if (mode === "inactive") return baseFetch(input, init);
    const request = new Request(input, init);
    const url = new URL(request.url);
    if (url.origin !== origin) return baseFetch(input, init);
    if (["/functions/v1/prepare-manual-entry-subject"].includes(url.pathname)) {
      throw new Error("Manual enrichment is unavailable in bounded verification");
    }
    if (url.pathname === "/functions/v1/create-checkout") {
      const preview: unknown = await request.clone().json();
      if (mode !== "active" || preview === null || typeof preview !== "object" || !("previewOnly" in preview) || preview.previewOnly !== true) {
        throw new Error("A new packet purchase is not authorized in bounded verification");
      }
      return baseFetch(input, init);
    }
    if (url.pathname === "/functions/v1/get-guest-packet") {
      const body: unknown = await request.clone().json();
      if (mode !== "active" || request.method !== "POST" || body === null || typeof body !== "object" ||
          !("mode" in body) || body.mode !== "artifacts") {
        throw new Error("Only retained packet artifact reads are available in existing-data verification");
      }
      return baseFetch(input, init);
    }
    if (url.pathname !== "/functions/v1/property-lookup") {
      if (url.pathname.startsWith("/functions/v1/")) {
        throw new Error("This operation is unavailable in existing-data verification");
      }
      return baseFetch(input, init);
    }
    if (mode === "invalid" || request.method !== "POST") throw new Error("Keeney verification policy is invalid; lookup was not dispatched");
    const body: unknown = await request.clone().json();
    if (body === null || typeof body !== "object" || Array.isArray(body) || !("type" in body) ||
        !["lookup", "comps", "independent_sales"].includes(String(body.type)) || "providerSpendPolicy" in body) {
      throw new Error("This lookup operation is unavailable in bounded Keeney verification");
    }
    const address = body.type === "lookup" ? ("address" in body ? body.address : undefined) :
      ("subjectAddress" in body ? body.subjectAddress : undefined);
    if (!isKeeneyVerificationAddress(address) || (body.type !== "lookup" &&
        (!("countyId" in body) || body.countyId !== "cook_il" || !("state" in body) || body.state !== "IL"))) {
      throw new Error("Keeney verification subject mismatch; lookup was not dispatched");
    }
    const restricted = { ...body, excludedLiveProviders: ["attom"], keeneyVerificationPolicy: KEENEY_EXISTING_DATA_POLICY };
    const headers = new Headers(request.headers); headers.set("Content-Type", "application/json");
    return baseFetch(new Request(request, { headers, body: JSON.stringify(restricted) }));
  };
}
